import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { Field, Form } from 'react-final-form';
import axios from 'axios';
import moment from 'moment-timezone';
import { ApolloProvider, useQuery } from '@apollo/client';
import useFlags from '@/hooks/useFlags';
import { GradeByType } from '@/components/staffer/NotebookGradeByMenu/types';
import { LmsProvider } from '@/components/shared/Lms/types';
import * as Routes from 'modules/routes';
import apolloClient from 'components/common/ApolloClient';
import { RequiredAsterisk } from 'components/common/Forms/Utils';
import SubmitButton from 'components/common/Forms/SubmitButton';
import useAssignablesStore from '@/components/staffer/AssignmentModal/WhatSection/store/useAssignablesStore';
import WhatSection from '../WhatSection';
import WhenSection from '../WhenSection';
import GradeBySection from '../GradeBySection';
import LtiSyncInfoPanel from './LtiSyncInfoPanel';
import styles from './LtiAssign.module.scss';
import { buildAssignmentsModels, nextJune30, GET_ASSIGNMENT_MODAL_DATA } from '../Utils';

type ProgramType = {
  display_investigations: boolean,
  display_investigations_text_section: boolean,
  display_unit_content: boolean,
  label: string,
  science: boolean,
  value: number,
};

type LtiAssignmentsModalProps = {
  lineItemsUrl?: string,
  lmsProvider?: LmsProvider | null,
  ltiAuthenticationId?: number,
  programs: ProgramType[],
  stafferId: number,
};

const formChangeValue = (args, state, utils) => {
  utils.changeValue(state, args[1], () => args[0]);
};

const formHandleSelect = (args, state, utils) => {
  formChangeValue(args, state, utils);
};

const LtiAssignForm = ({ programs, stafferId,
  ltiAuthenticationId, lmsProvider, lineItemsUrl,
}: LtiAssignmentsModalProps) => {
  const [programId, setProgramId] = useState(programs[0].value);
  const [submitting, setSubmitting] = useState(false);
  const games = useAssignablesStore(state => state.games);
  const selectedUnitLesson = useAssignablesStore(state => state.selectedUnitLesson);

  const { loading, data, error } = useQuery(
    GET_ASSIGNMENT_MODAL_DATA,
    { variables: { programId, stafferId } },
  );
  const { usesAssignmentsBasedGrading } = useFlags();

  useEffect(() => {
    if (!programId) return;

    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [data]);

  const onSubmit = (values) => {
    const notebookAndReadingToAssign = Object.keys(values)
      .filter(key => key.includes('notebook-reading'))
      .map(key => values[key]);

    let formattedModelsToAssign;
    const isSectionNotebook = values.type === 'sectionNotebook';

    let [gradingBy, maxScore] = [null, null];
    if (isSectionNotebook && (usesAssignmentsBasedGrading)) {
      gradingBy = values.grading_notebook_by;
      const isGradeByAssignment = gradingBy === GradeByType.Assignment || usesAssignmentsBasedGrading;
      if (isGradeByAssignment) maxScore = values.max_score;
    }

    if (isSectionNotebook && notebookAndReadingToAssign.length > 0) {
      formattedModelsToAssign = notebookAndReadingToAssign;
    } else if (Array.isArray(values.modelsToAssign)) {
      formattedModelsToAssign = [...values.modelsToAssign];
    } else {
      formattedModelsToAssign = [values.modelsToAssign];
    }

    const requestData = {
      assignment: {
        dates_enforced: false,
        due_datetime: values?.due_datetime?.value ? new Date(values?.due_datetime?.value) : nextJune30(),
        grading_notebook_by: gradingBy,
        line_items_url: lineItemsUrl,
        lms_provider_id: lmsProvider?.id,
        lms_type: lmsProvider ? 'LTI' : 'schoology',
        max_score: maxScore,
        name: '',
        notes: '',
        program_id: programId,
        start_datetime: new Date(),
      },
      assignments_models: buildAssignmentsModels(
        formattedModelsToAssign.filter(toAssign => toAssign !== undefined).flat(), values.lesson_id, selectedUnitLesson.isUnit,
      ),
      classroom_ids: data.staffer.classrooms.map(classroom => classroom.id),
      student_ids: [],
    };

    setSubmitting(true);

    const assignmentSelectionForm = document.getElementById('assignment_selection') as HTMLFormElement;

    return axios
      .post(Routes.plato_api_assignments_path({
        lms_provider_id: lmsProvider?.id,
        lti_authentication_id: ltiAuthenticationId,
        lti_return_url: assignmentSelectionForm.action,
      }), requestData)
      .then((response) => {
        const contentItemsInput = document.getElementById('content_items') as HTMLInputElement;
        const jwtInput = document.getElementById('jwt') as HTMLInputElement;

        if (contentItemsInput) {
          const assessmentName = document.getElementById('assessment_name') as HTMLInputElement;
          const contentFormValue = contentItemsInput.value;

          contentItemsInput.value = contentFormValue.replace(/:lti_link/g, response.data.data.lti_link);
          assessmentName.value = response.data.data.lti_title || 'TCI Assignment';
        } else if (jwtInput) {
          const contentFormValue = jwtInput.value;

          jwtInput.value = contentFormValue.replace(/:jwt_token/g, response.data.data.jwt_token);
        }

        assignmentSelectionForm.submit();
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        setSubmitting(false);
      });
  };

  const findProgram = () => {
    if (!programId) return undefined;

    return programs.find(program => program.value === programId);
  };

  const getInitialValues = () => (
    Object.assign(
      {
        assessment_ids: null,
        classroom_ids: [],
        grading_notebook_by: null,
        investigation: [],
        lesson_game: [],
        max_score: null,
        modelsToAssign: [],
        section_notebook: [],
        start_datetime: moment().utc().format('YYYY-MM-DD H:mm:ss UTC'),
        student_ids: [],
        unit_projects: [],
      },
    )
  );

  const getInitialLessonId = () => {
    const lessons = data.program.lessons.slice().filter(lesson => !!lesson.position);

    if (lessons && lessons.length > 0) {
      return lessons.sort((a, b) => parseInt(a.position, 10) - parseInt(b.position, 10))[0].id;
    }
    return data.program.lessons[0].id;
  };

  const hasErrors = (values) => {
    const noModelsToAssign = !(values.modelsToAssign && values.modelsToAssign.length);
    const noNotebookAndReadingToAssign = Object.keys(values)?.filter(key => key.includes('notebook-reading'))?.length === 0;
    const noDueDate : boolean = !values?.due_datetime;

    return (noModelsToAssign && noNotebookAndReadingToAssign) || noDueDate;
  };

  const showGradeBySection = values => (
    !usesAssignmentsBasedGrading && values.type === 'sectionNotebook'
  );

  return (
    <>
      <div className={styles.contentContainer}>
        <div className={styles.content}>
          <p className={styles.content}>
            Don't know what you're looking for?
          </p>
          <a href={Routes.home_teacher_programs_path()} target="_blank">
            Browse content <i className="fa fa-external-link" />
          </a>
        </div>
        <LtiSyncInfoPanel kind={lmsProvider?.kind} />
      </div>
      <Form
        mutators={{
          changeValue: formChangeValue,
          handleSelect: formHandleSelect,
        }}
        render={({ form, handleSubmit, values }) => (
          <form
            aria-label="lti-assign-form"
            className={styles.form}
            onSubmit={handleSubmit}
          >
            <h1>Select Content to Assign</h1>
            <div className={styles.row}>
              <Field
                name="program"
                render={() => (
                  <div className={`mb10 ${styles.field}`}>
                    <label htmlFor="program-id">
                      <p>Program</p>
                      <RequiredAsterisk />
                    </label>
                    <Select
                      required
                      searchable
                      aria-label="program-select"
                      className={styles.select}
                      id="program-id"
                      isLoading={loading}
                      label="program-id"
                      options={programs.map(p => ({ ...p, label: p.label }))}
                      value={findProgram()}
                      onChange={program => setProgramId(program.value)}
                    />
                  </div>
                )}
              />
            </div>

            {data && (
              <WhatSection
                openAssessmentInNewTab
                form={form}
                formChangeValue={form.mutators.changeValue}
                initialLessonId={parseInt(getInitialLessonId(), 10)}
                initialValues={getInitialValues()}
                programID={programId}
                showWhatTitle={false}
                stafferId={stafferId}
              />
            )}

            {showGradeBySection(values) && <GradeBySection />}

            <WhenSection
              form={form}
              formHandleSelect={form.mutators.handleSelect}
              games={games}
              ltiKind={lmsProvider?.kind ?? null}
              modelType={form.getState().values.type}
              showWhenTitle={false}
            />

            <SubmitButton
              buttonClassName={`btn btn--purple ${styles.submitButton}`}
              submitButtonText="Assign"
              submitDisabled={hasErrors(values)}
              submitting={submitting}
              submittingText="Submitting..."
            />
          </form>
        )}
        onSubmit={onSubmit}
      />
    </>
  );
};

const LtiAssign = ({ lineItemsUrl, lmsProvider, ltiAuthenticationId, programs, stafferId,
}: LtiAssignmentsModalProps) => (
  <ApolloProvider client={apolloClient}>
    <LtiAssignForm {... { lineItemsUrl, lmsProvider, ltiAuthenticationId, programs, stafferId }} />
  </ApolloProvider>
);

export default LtiAssign;
